import React from "react";
import { BoardCard as BoardCardType } from "containers/project-boards/types";
import { boardGradient } from "containers/project-boards/board-color";
import { StarIcon, PinIcon } from "./icons";

interface BoardRowProps {
  board: BoardCardType;
  onOpen: (id: string) => void;
  onToggleStar: (board: BoardCardType) => void;
  onTogglePin: (board: BoardCardType) => void;
}

// The pin and star show only on hover, which a touch screen does not have: there they are always
// drawn and big enough for a finger (the negative margin keeps the row from growing taller).
const TOUCH_ICON =
  "[@media(hover:none)]:-my-2 [@media(hover:none)]:p-3.5 [@media(hover:none)]:opacity-100";

function BoardRow({ board, onOpen, onToggleStar, onTogglePin }: BoardRowProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(board.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(board.id);
        }
      }}
      className="group flex cursor-pointer items-center gap-3 rounded-lg border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] px-3 py-2 transition hover:bg-[var(--p-color-bg-surface-secondary)]"
    >
      <span
        className="h-7 w-11 shrink-0 rounded-md"
        style={{ background: boardGradient(board) }}
      />
      <span className="flex-1 truncate text-[13px] font-medium text-[var(--p-color-text)]">
        {board.name}
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onTogglePin(board);
        }}
        aria-label={board.isPinned ? "Unpin board" : "Pin board"}
        title={board.isPinned ? "Unpin" : "Pin"}
        className={`rounded p-1 transition hover:bg-[var(--p-color-bg-surface-tertiary)] ${TOUCH_ICON} ${
          board.isPinned
            ? "text-[var(--p-color-text)]"
            : "text-[var(--p-color-text-secondary)] opacity-0 group-hover:opacity-100"
        }`}
      >
        <PinIcon filled={board.isPinned} />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleStar(board);
        }}
        aria-label={board.isStarred ? "Unstar board" : "Star board"}
        title={board.isStarred ? "Unstar" : "Star"}
        className={`rounded p-1 transition hover:bg-[var(--p-color-bg-surface-tertiary)] ${TOUCH_ICON} ${
          board.isStarred
            ? "text-amber-400"
            : "text-[var(--p-color-text-secondary)] opacity-0 group-hover:opacity-100"
        }`}
      >
        <StarIcon filled={board.isStarred} />
      </button>
    </div>
  );
}

export default React.memo(BoardRow);
